import { observable, action, computed, reaction } from 'mobx';
import lunr from 'lunr';
import isFunction from 'lodash/isFunction';
import flatten from 'lodash/flatten';
import uniqWith from 'lodash/uniqWith';
import uniq from 'lodash/uniq';

import { api } from '../services';

import { noticesStore } from './index';
import { filterStore } from 'air-filter-mobx-store';
import lockbeatStore from './lockbeatStore';

const uniqByField = fieldname => (el1, el2) => el1[fieldname] === el2[fieldname];
const sortByField = fieldname => (el1, el2) => (el1[fieldname] === el2[fieldname] ? 0 : el1[fieldname] < el2[fieldname] ? -1 : 1);
const mapByField = fieldname => el => el[fieldname];
const floorMappingFn = num => Math.floor(num);
const ceilMappingFn = num => Math.ceil(num);

class CarsStore {
  @observable cars = [];
  @observable filters = {};
  @observable appliedFilters = {};
  @observable fullTextSearch = null;
  @observable loadedAt;

  constructor(filterStore, noticesStore) {
    this.filterStore = filterStore;
    this.noticesStore = noticesStore;
    this.lockbeatStore = lockbeatStore;
    reaction(
      () => this.filterStore.globalFilterFunction,
      fn => {
        const cars = this.cars.map(car => {
          if (fn(car)) {
            return { ...car, hidden: false };
          } else {
            return { ...car, hidden: true };
          }
        });
        this.setCars(cars);
      }
    );
  }

  buildIndex(cars) {
    this.fullTextSearchIndex = lunr(function() {
      this.field('client.fullName');
      this.field('plate');
      this.field('make.descrizione');
      this.field('model.descrizione');
      this.ref('id');
      cars.forEach(car => {
        car['client.fullName'] = car.client.fullName;
        car['make.descrizione'] = car.make.descrizione;
        car['model.descrizione'] = car.model.descrizione;
        this.add(car);
      });
    });
    return cars;
  }

  @action
  load() {
    return api('get cars').then(cars => {
      this.noticesStore.carsHaving = cars.filter(car => (car.notifications||[]).length > 0);
      //this.setCars(cars.slice(0, 10));
      this.setCars(cars);
      this.setLoadedAt();

      setTimeout(() => {
        this.buildIndex(cars);
      }, 500);
    });
  }

  @action
  loadMinimal() {
    let filters = this.appliedFilters;
    if (this.fullTextSearch !== null) {
      filters.fullTextSearch = this.fullTextSearch;
    }
    return api('get cars minimal',
      filters,
      this.pageSize,
      this.page).then(res => {
      this.setCarsCount(res.count);
      this.setCars(res.rows);
      this.setLoadedAt();

    });
  }

  @action
  async updateCar(plate, data) {
    const updatedCar = await api('update car', plate, data);

    const carIndex = this.cars.findIndex(car => car.plate === plate);

    if (carIndex !== -1) {
      // Create a new array with the updated car
      const updatedCars = [...this.cars];

      // Replace the old car with the updated car
      updatedCars[carIndex] = {
        ...this.cars[carIndex],
        ...updatedCar
      };

      // Update the cars array
      this.setCars(updatedCars);
    }

    return updatedCar;
  }

  @action
  loadGroupsCount() {
    let filters = this.appliedFilters;
    if (this.fullTextSearch !== null) {
      filters.fullTextSearch = this.fullTextSearch;
    }
    return api('get groups count',
      filters
      ).then(counts => {
      this.setGroupsCount(counts);

    });
  }

  @action
  loadFilters() {
    let filters = this.appliedFilters;
    if (this.fullTextSearch !== null) {
      filters.fullTextSearch = this.fullTextSearch;
    }
    return api('get cars filters', filters).then(filters => {
      this.setFilters(filters);
    });
  }

  @action
  applyFilters() {
    this.page = 1;
    this.loadMinimal();
    this.loadGroupsCount();
  }

  @action
  resetFilters() {
    this.setAppliedFilters({});
    this.applyFilters();
  }

  @action
  setAppliedFilters(filters) {
    this.appliedFilters = filters;
  }

  @action
  setFullTextSearch(fullTextSearch) {
    this.fullTextSearch = fullTextSearch;
    this.applyFilters();
  }

  @action
  setFilters(filters) {
    this.filters = filters;
  }

  @action setLoadedAt() {
    this.loadedAt = new Date().getTime();
  }

  @action
  setCars(cars) {
    this.cars = cars;
  }

  @action
  setCarsCount(carsCount) {
    this.carsCount = carsCount;
  }

  @action
  setGroupsCount(groupsCount) {
    this.groupsCount = groupsCount;
  }

  @computed get filtered() {
    if (this.filterStore.globalFilterFunction) {
      return this.cars.filter(this.filterStore.globalFilterFunction);
    } else {
      return this.cars;
    }
  }

  filterLength = (filterType, attribute) => {
    if (isFunction(this.filterStore.predictionFns[`${filterType}.${attribute}`])) {
      return this[attribute].filter(this.filterStore.predictionFns[`${filterType}.${attribute}`]).length;
    }
    return this[attribute].length;
  };

  groupsLength = (filterType, attribute) => {
    if (attribute === 'all' && isFunction(this.filterStore.predictionFns[`${filterType}.${attribute}`])) {
      return this.cars.filter(car => car.groupId !== -1).filter(this.filterStore.predictionFns[`${filterType}.${attribute}`]).length;
    }
    if (attribute === 'orphans' && isFunction(this.filterStore.predictionFns[`${filterType}.${attribute}`])) {
      return this.cars.filter(car => car.groupId === null).filter(this.filterStore.predictionFns[`${filterType}.${attribute}`]).length;
    }
    if (isFunction(this.filterStore.predictionFns[`${filterType}.${attribute}`])) {
      return this.cars.filter(car => car.groupId === attribute).filter(this.filterStore.predictionFns[`${filterType}.${attribute}`]).length;
    }
    return this.cars.filter(car => car.groupId === attribute).length;
  };

  groupsCountLength = (attribute) => {
    if (attribute === 'all') {
      return this.groupsCount.reduce((acc, curr) => {
          return acc = acc + parseInt(curr.count)
      }, 0);
    }
    if (attribute === 'orphans') {
      return this.groupsCount.filter(group => group.groupId === null).map(mapByField('count'))[0] || 0;
    }
    return this.groupsCount.filter(group => group.groupId === attribute).map(mapByField('count'))[0] || 0;
  };

  @computed get groups() {
    return uniqWith(this.cars.filter(car => car.group).map(car => car.group), (g1, g2) => g1.id === g2.id);
  }

  getModelsFromMake(codiceMarca) {
    if (codiceMarca) {
      const allmodels = this.filtered.filter(car => car.make.codiceMarca === codiceMarca).map(mapByField('model'));
      return uniqWith(allmodels, uniqByField('codiceModello')).sort(sortByField('descrizione'));
    }
    return [];
  }

  getModelsFromMakeDescription(codiceMarca) {
    if (codiceMarca) {
      const allmodels = this.cars.filter(car => car.make.codiceMarca === codiceMarca).map(mapByField('model'));
      return uniqWith(allmodels, uniqByField('codiceModello')).sort(sortByField('descrizione'));
    }
    return [];
  }

  @computed
  get makes() {
    return uniqWith(this.filtered.map(mapByField('make')).filter(make => !!make.codiceMarca), uniqByField('codiceMarca')).sort(sortByField('descrizione'));
  }

  @computed
  get makesDescription() {
    return uniqWith(this.cars.map(mapByField('make')).filter(make => !!make.codiceMarca), uniqByField('codiceMarca')).sort(sortByField('descrizione'));
  }

  @computed
  get models() {
    return flatten(this.makesWithModels.map(mapByField('models')));
  }

  @computed
  get modelsDescription() {
    return flatten(this.makesWithModelsDescription.map(mapByField('models')));
  }

  @computed
  get makesWithModels() {
    return this.makes.map(make => ({ ...make, models: this.getModelsFromMake(make.codiceMarca) }));
  }

  @computed
  get makesWithModelsDescription() {
    return this.makesDescription.map(make => ({ ...make, models: this.getModelsFromMakeDescription(make.codiceMarca) }));
  }

  @computed get disconnected() {
    return this.cars.filter(this.disconnectedFilterFn);
  }

  @computed get withAlarms() {
    return this.cars.filter(this.withAlarmsFilterFn);
  }

  @computed get withDtcs() {
    return this.cars.filter(this.withDtcsFilterFn);
  }

  @computed get withMaintenance() {
    return this.cars.filter(this.withMaintenanceFilterFn);
  }

  @computed get withBatteryDown() {
    return this.cars.filter(this.withBatteryDownFilterFn);
  }

  @computed get withEngineOn() {
    return this.cars.filter(this.withEngineOnFilterFn);
  }

  @computed get withEngineOff() {
    return this.cars.filter(this.withEngineOffFilterFn);
  }

  @computed get withNotices() {
    return this.cars.filter(this.withNoticesFilterFn);
  }

  @computed get withLockOn() {
    return this.cars.filter(this.withLockOnFilterFn);
  }

  @computed get withLockOff() {
    return this.cars.filter(this.withLockOffFilterFn);
  }

  disconnectedFilterFn = car => car.status === 'disconnected';

  withDtcsFilterFn = car => (car.dtcs && car.dtcs.filter(dtc => dtc.severity === 'red' || dtc.severity === 'orange').length > 0);

  withAlarmsFilterFn = car => (car.alarms && car.alarms.length > 0);

  withMaintenanceFilterFn = car => (car.maintenances && car.maintenances.length > 0);

  withBatteryDownFilterFn = car => (car.battery && car.battery.value < 11000 && car.battery.updatedAt && car.battery.updatedAt != '');

  withEngineOnFilterFn = car => !!car.engineon;

  withEngineOffFilterFn = car => !car.engineon;

  withNoticesFilterFn = car => (this.noticesStore.carsHaving || []).findIndex(notice => notice.imei === car.imei) > -1;

  withLockOnFilterFn = car => !this.lockbeatStore.imeiHasNoLockbeat(car.imei);

  withLockOffFilterFn = car => this.lockbeatStore.imeiHasNoLockbeat(car.imei);

  fullTextSearchFns = text => {
    let matches = this.fullTextSearchIndex.search(text);
    return {
      search: car => matches.findIndex(match => match.ref === car.id) > -1,
      sort: (car1, car2) => {
        let score1 = matches.find(match => match.ref === car1.id).score;
        let score2 = matches.find(match => match.ref === car2.id).score;
        return score2 - score1;
      }
    };
  };

  getModelsFromBrand(brandcode) {
    if (brandcode) {
      const allmodels = this.filtered.filter(car => car.make.codiceMarca === brandcode).map(mapByField('model'));
      return uniqWith(allmodels, uniqByField('codiceModello')).sort(sortByField('descrizione'));
    }
    return [];
  }

  @computed
  get brands() {
    return uniqWith(this.filtered.map(mapByField('make')), uniqByField('codiceMarca')).sort(sortByField('descrizione'));
  }

  @computed
  get brandsWithModels() {
    return this.brands.map(brand => ({ ...brand, models: this.getModelsFromBrand(brand.codiceMarca) }));
  }

  @computed
  get alimentations() {
    return uniq(this.filtered.map(mapByField('alimentation'))).sort();
  }

  @computed get odometers() {
    return this.filtered
      .map(car => {
        if (car.km) {
          return car.km;
        } else {
          return -1;
        }
      })
      .filter(odo => odo > -1);
  }

  @computed get totalKmMin() {
    return this.odometers.length > 0 ? Math.min(...this.odometers.map(floorMappingFn)) : 0;
  }

  @computed get totalKmMax() {
    return this.odometers.length > 0 ? Math.max(...this.odometers.map(ceilMappingFn)) : 0;
  }

  @computed get registrationYears() {
    return this.filtered.map(car => (car.registrationYear ? car.registrationYear : -1)).filter(odo => odo > -1);
  }

  @computed get registrationYearMin() {
    return Math.min(...this.registrationYears);
  }

  @computed get registrationYearMax() {
    return Math.max(...this.registrationYears);
  }

  @action setEta = (car, { time, distance }) => {
    const cars = this.cars.map(itemCar => {
      if (itemCar.id === car.id) {
        return { ...itemCar, eta: { time, distance } };
      } else {
        return itemCar;
      }
    });
    this.setCars(cars);
  };

  @action clearEta = car => {
    const cars = this.cars.map(itemCar => {
      if (itemCar.id === car.id) {
        delete itemCar.eta;
      }
      return itemCar;
    });
    this.setCars(cars);
  };

  @action clearAllEta = car => {
    const cars = this.cars.map(itemCar => {
      delete itemCar.eta;
      return itemCar;
    });
    this.setCars(cars);
  };
}

export default new CarsStore(filterStore, noticesStore, lockbeatStore);



// WEBPACK FOOTER //
// ./src/stores/carsStore.js